import { FC, useContext } from 'react';
import { Cascader, theme, Typography } from 'antd';
import type { CascaderProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import {
    ColumnWidthOutlined,
    ColumnHeightOutlined,
    PlusOutlined,
    InfoCircleOutlined,
    FormOutlined,
    TeamOutlined,
    PaperClipOutlined,
    TableOutlined,
    ProfileOutlined,
    BlockOutlined,
    ThunderboltOutlined,
    LinkOutlined
} from '@ant-design/icons';

import { FieldTypeContext } from '../../../providers/FieldTypeContext';
import { useCompositeFieldTypes } from '../../CompositeField/useCompositeFieldTypes';
import {
    UIElementType,
    EditorUIContainer,
    EditorUIElement,
    Entity
} from '#types/System.types';
import { LayoutEditorContext } from '../../../providers/editor/LayoutEditorContext';
import { MetaFieldUIDescription, CONTAINER_ELEMENT_TYPES, RecordStatus } from '#types/System.Parameters.types';
import { parseReferencedListTargets } from '#types/System.ReferencedList.types';
import { useGetEntityGroups } from '../../../use/useGetEntityGroups';
import { useReferencedListConfig } from '../../../use/useReferencedListConfig';

let idCounter = 0;

/**
 * Generates a unique ID for new elements
 * @returns {string} A unique identifier prefixed with 'a'
 */
const generatedId = () => `a${idCounter++}`;

const { Text } = Typography;

interface AddElementMenuProps extends CascaderProps {
    /** Additional style overrides */
    style?: React.CSSProperties;
}

/**
 * AddElementMenu is a cascading dropdown menu component that allows users to add
 * different types of elements to the layout editor. It supports adding containers
 * (rows/columns), fields, entity references, meta fields, and form properties.
 * Uses dynamic field types from FieldTypeProvider for both system and custom fields.
 * 
 * @component
 * @param {CascaderProps} props - Ant Design Cascader component props
 * @returns {JSX.Element | null} The rendered AddElementMenu component or null if no editor context
 */
export const AddElementMenu: FC<AddElementMenuProps> = ({
    ...props
}) => {
    const editor = useContext(LayoutEditorContext);
    const {
        fieldTypes,
        isLoading,
    } = useContext(FieldTypeContext);
    const compositeFieldTypesArray = useCompositeFieldTypes();
    const { token } = theme.useToken();
    const { groupOptions } = useGetEntityGroups();
    const { config: referencedListConfig, loading: referencedListConfigLoading } = useReferencedListConfig();

    if (!editor) return null;

    const {
        selectedElement,
        layout,
        addElement,
        tableTypes,
        structure,
        entities
    } = editor;

    if (isLoading || !fieldTypes) {
        return (
            <Cascader
                placeholder={<Text>Loading field types...</Text>}
                style={{ 
                    width: '200px', 
                    ...props.style 
                }}
                disabled={true}
                size={props.size}
            />
        );
    }

    const isContainer = (element: EditorUIElement): element is EditorUIContainer => 
        CONTAINER_ELEMENT_TYPES.includes(element.uiElementType);

    const targetContainer = selectedElement && isContainer(selectedElement)
        ? selectedElement 
        : layout.structure as EditorUIContainer;

    if (!targetContainer) return null;

    const handleAddContainer = (elementType: UIElementType.Grid | UIElementType.Row | UIElementType.Column) => {
        const newContainer: EditorUIContainer = {
            uiElementType: elementType,
            children: [],
            props: {},
            dndId: generatedId()
        };

        addElement(newContainer, targetContainer);
    };

    const handleAddField = (fieldId: string) => {
        const newElement: EditorUIElement = {
            fieldId: fieldId,
            uiElementType: UIElementType.Field,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    /**
     * Adds an entity reference element to the target container
     * @param {Entity["docId"]} entityId - The ID of the entity to reference
     * @param {string | null} entityGroupId - Optional group ID for the entity
     */
    const handleAddEntity = (entityId: Entity["docId"], entityGroupId: string | null = null) => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.EntityReference,
            entityId,
            entityGroupId,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        if (entityGroupId) {
            (newElement as any).entityGroupId = entityGroupId;
        }

        addElement(newElement, targetContainer);
    };

    /**
     * Adds an embedded entity element to the target container. Mirrors
     * {@link handleAddEntity} but renders the linked record's full fields inline.
     * @param {Entity["docId"]} entityId - The ID of the entity to embed
     * @param {string | null} entityGroupId - Optional group ID for the entity
     */
    const handleAddEmbeddedEntity = (entityId: Entity["docId"], entityGroupId: string | null = null) => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.EmbeddedEntity,
            entityId,
            entityGroupId,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const handleAddMetaField = (id: string) => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.MetaField,
            id,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const handleAddFormProperty = (id: string) => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.FormProperty,
            id,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const handleAddAttachments = () => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.Attachments,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const handleAddReferencedList = () => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.ReferencedList,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    /**
     * Adds a table element to the target container
     * @param {string} tableId - The ID of the table type to add
     */
    const handleAddTable = (tableId: string) => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.Table,
            tableId,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const handleAddButton = () => {
        const newElement: EditorUIElement = {
            uiElementType: UIElementType.Button,
            props: {},
            dndId: generatedId()
        } as EditorUIElement;

        addElement(newElement, targetContainer);
    };

    const isFormEditor = editor.type === 'form';

    /**
     * A Referenced By element can only ever fill on a record type some declared relation targets,
     * so elsewhere it is offered but disabled rather than hidden. Only the `'entity'` branch of the
     * editor union carries an entity id — the `'form'` branch's `selectedStructureId` is a form
     * type id in a different key space and would match a target by accident.
     *
     * While the config document is still loading the answer is unknown, so the label says so
     * instead of asserting the negative — the option stays disabled either way, matching the
     * "Loading field types..." gate above. The label must stay a plain string: `showSearch.filter`
     * casts it to `string` and lowercases it.
     */
    const isReferencedListTarget =
        editor.type === 'entity'
        && !!editor.selectedStructureId
        && parseReferencedListTargets(referencedListConfig).has(editor.selectedStructureId);

    const referencedListLabel = referencedListConfigLoading
        ? 'Referenced By (Loading declared relations...)'
        : isReferencedListTarget
            ? 'Referenced By'
            : 'Referenced By (No relation targets this record type)';

    const compositeFieldOptions = compositeFieldTypesArray
        .filter(cft => cft.docId && cft.status === RecordStatus.Active)
        .map(cft => ({
            value: `composite-${cft.docId}`,
            label: cft.description?.shortLabel || cft.docId || 'Unnamed',
        }));

    const fieldOptions = Object.entries(fieldTypes)
        .filter(([, field]) => field.fieldDataTypeId !== 'composite')
        .map(([id, field]) => ({
            value: `field-${id}`,
            label: field.description?.shortLabel || id,
        }));

    const referenceOptions = entities.map(entity => ({
        value: `reference-${entity.docId}-null`,
        label: entity.description?.shortLabel || 'Unnamed',
    }));

    const metaFieldOptions = Object.entries(MetaFieldUIDescription).map(([id, field]) => ({
        value: `meta-${id}`,
        label: field.shortLabel || id,
    }));

    const formPropertyOptions = [
        {
            value: `form-formNumber`,
            label: 'Form Number',
        },
        {
            value: `form-formType`,
            label: 'Form Type',
        },
        {
            value: `form-formVersionNumber`,
            label: 'Form Version Number',
        }
    ];

    const tableOptions = tableTypes.map(table => ({
        value: `table-${table.docId}`,
        label: table.description?.shortLabel || table.docId,
    }));

    /**
     * Prepares entity group options by maintaining their parent entity categories
     */
    const entityGroupOptions: DefaultOptionType[] = groupOptions
        .filter(group => group.label !== 'Entities')
        .map(group => ({
            label: group.label,
            value: `group-category-${group.label}`,
            children: group.options.map(option => {
                try {
                    const parsed = JSON.parse(option.value);
                    return {
                        value: `reference-${parsed.entityId}-${parsed.entityGroupId}`,
                        label: option.label,
                    } as DefaultOptionType;
                } catch (e) {
                    console.error('Error parsing group option:', e);
                    return undefined;
                }
            }).filter((option): option is DefaultOptionType => option !== undefined)
        }));

    const embeddedReferenceOptions = entities.map(entity => ({
        value: `embedded-${entity.docId}-null`,
        label: entity.description?.shortLabel || 'Unnamed',
    }));

    const embeddedEntityGroupOptions: DefaultOptionType[] = groupOptions
        .filter(group => group.label !== 'Entities')
        .map(group => ({
            label: group.label,
            value: `embedded-group-category-${group.label}`,
            children: group.options.map(option => {
                try {
                    const parsed = JSON.parse(option.value);
                    return {
                        value: `embedded-${parsed.entityId}-${parsed.entityGroupId}`,
                        label: option.label,
                    } as DefaultOptionType;
                } catch (e) {
                    console.error('Error parsing group option:', e);
                    return undefined;
                }
            }).filter((option): option is DefaultOptionType => option !== undefined)
        }));

    const options: CascaderProps['options'] = [
        {
            value: UIElementType.Row,
            label: 'Row Container',
            icon: <ColumnWidthOutlined />
        },
        {
            value: UIElementType.Column,
            label: 'Column Container',
            icon: <ColumnHeightOutlined />
        },
        {
            label: 'Fields',
            value: 'fields',
            children: fieldOptions
        },
        {
            label: 'Records',
            value: 'entities',
            children: referenceOptions
        },
        {
            label: 'Record Groups',
            value: 'entity-groups',
            icon: <TeamOutlined />,
            children: entityGroupOptions
        },
        ...(isFormEditor ? [{
            label: 'Embedded Records',
            value: 'embedded-entities',
            icon: <ProfileOutlined />,
            children: embeddedReferenceOptions
        }, {
            label: 'Embedded Record Groups',
            value: 'embedded-entity-groups',
            icon: <BlockOutlined />,
            children: embeddedEntityGroupOptions
        }] : []),
        {
            label: 'Meta Fields',
            value: 'meta-fields',
            icon: <InfoCircleOutlined />,
            children: metaFieldOptions
        },
        {
            label: 'Form Properties',
            value: 'form-properties',
            icon: <FormOutlined />,
            children: formPropertyOptions
        },
        {
            value: 'attachments',
            label: 'Attachments',
            icon: <PaperClipOutlined />
        },
        ...(!isFormEditor ? [{
            value: 'referencedList',
            label: referencedListLabel,
            icon: <LinkOutlined />,
            disabled: !isReferencedListTarget
        }] : []),
        ...(isFormEditor ? [{
            value: 'button',
            label: 'Button',
            icon: <ThunderboltOutlined />
        }] : []),
        ...(isFormEditor && compositeFieldOptions.length > 0 ? [{
            label: 'Composite Fields',
            value: 'composite-fields',
            icon: <FormOutlined />,
            children: compositeFieldOptions,
        }] : []),
        ...(tableTypes.length > 0 ? [{
            label: 'Tables v2',
            value: 'tables',
            icon: <TableOutlined />,
            children: tableOptions
        }] : [{
            label: 'Tables v2 (No tables available)',
            value: 'tables-empty',
            icon: <TableOutlined />,
            disabled: true,
        }])
    ];

    const onChange = (value: string[]) => {
        if (!value || value.length === 0) return;
        
        const selectedValue = value[value.length - 1];
        
        if (selectedValue === UIElementType.Row || selectedValue === UIElementType.Column) {
            handleAddContainer(selectedValue);
        } else if (selectedValue.startsWith('field-')) {
            const fieldId = selectedValue.split('-')[1];
            handleAddField(fieldId);
        } else if (selectedValue.startsWith('reference-')) {
            const parts = selectedValue.split('-');
            const entityId = parts[1];
            const entityGroupId = parts[2] === 'null' ? null : parts[2];
            handleAddEntity(entityId, entityGroupId);
        } else if (selectedValue.startsWith('embedded-')) {
            const parts = selectedValue.split('-');
            const entityId = parts[1];
            const entityGroupId = parts[2] === 'null' ? null : parts[2];
            handleAddEmbeddedEntity(entityId, entityGroupId);
        } else if (selectedValue.startsWith('meta-')) {
            const id = selectedValue.split('-')[1];
            handleAddMetaField(id);
        } else if (selectedValue.startsWith('form-')) {
            const id = selectedValue.split('-')[1];
            handleAddFormProperty(id);
        } else if (selectedValue === 'attachments') {
            handleAddAttachments();
        } else if (selectedValue === 'referencedList') {
            handleAddReferencedList();
        } else if (selectedValue === 'button') {
            handleAddButton();
        } else if (selectedValue.startsWith('composite-')) {
            const compositeFieldId = selectedValue.replace('composite-', '');
            handleAddField(compositeFieldId);
        } else if (selectedValue.startsWith('table-')) {
            const tableId = selectedValue.replace('table-', '');
            handleAddTable(tableId);
        }
    };

    return (
        <Cascader
            options={options}
            onChange={onChange as any}
            placeholder={<Text>Add to layout...</Text>}
            style={{ 
                width: '200px', 
                ...props.style 
            }}
            variant={'outlined'}
            changeOnSelect={false}
            displayRender={(labels) => labels[labels.length - 1]}
            showSearch={{
                filter: (inputValue, path) => {
                    return path.some(
                        option => (option.label as string)
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                    );
                }
            }}
            expandTrigger="hover"
            popupMatchSelectWidth={false}
            disabled={!structure}
            suffix={<PlusOutlined style={{ color: token.colorPrimary }} />}
            size={props.size}
        />
    );
}; 